import React, { useState } from "react";
import "../../../../../sass/components/dpa-tips.scss";
import { DPAType } from "../../../../common/service/server/dto/DataProtectionAssessmentDto";
import { useOrganizationVendors } from "../../../hooks";
import { isGoogleAnalytics } from "../../../util/vendors";
import { DataRecipientLogo } from "../../../components/DataRecipientLogo";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../get-compliant/data-recipients/shared";
import { VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER } from "../../../types/Vendor";
import { CategoryDto, DataMapDto } from "../../../../common/service/server/dto/datamap/DataMapDto";
import { collectedPICsFromFilteredCollection } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { getSensitivePICsWithFilters } from "../../../../common/service/resources/data-map/picHelpers";

export const DPATipsWhoSigns = () => {
  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>Who should sign the DPA?</p>
      </div>
      <div className="tip-content">
        <p>
          Some jurisdictions require a signature from someone at your organization. There is no
          requirement about who, specifically, must sign. A best practice is for the highest-ranking
          person at your business who was involved with the preparation and review of the DPA to
          sign it. Additional signatures may be added but are not necessary.
        </p>
      </div>
    </div>
  );
};

export const DPATipsWhatIs = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>What is a Data Protection Assessment?</p>
      </div>
      <div className="tip-content">
        {isExpanded === false && (
          <p>
            A Data Protection Assessment (DPA) is a document of an internal review that weighs the
            potential risks of a processing activity versus its benefits.
          </p>
        )}
        {isExpanded === true && (
          <>
            <p>
              A Data Protection Assessment (DPA) is a document that businesses must create before
              engaging in certain types of data processing. They are meant to identify and weigh the
              benefits to the controller, the consumer, other stakeholders, and the public against
              the potential risks to the rights of the consumer associated with the processing.
            </p>
            <p>
              A DPA must take into account the reasonable expectations of consumers, the context of
              the processing, and the relationship between the controller and the consumer whose
              personal data will be processed. It should also identify safeguards that can mitigate
              the risks, and consider whether the use of de-identified data would be more
              appropriate.
            </p>
            <p>
              DPAs are not public-facing documents, though they must be made available to
              enforcement authorities upon request. In that event, the authorities must keep them
              confidential.
            </p>
          </>
        )}
      </div>
      <div className="link-wrapper">
        <a
          className="read-more-link"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </a>
      </div>
    </div>
  );
};

export const DPAWhyAmISeeingThisCard = ({
  orgId,
  dpaType,
  dataMap,
}: {
  orgId: string;
  dpaType: DPAType;
  dataMap: DataMapDto;
}) => {
  // Use separate components for each DPA type since each type of help card has different data fetching hooks
  if (dpaType == "DATA_SELLING_OTHER") {
    return <DPAWhyAmISeeingThisCardWithDataSellingVendors orgId={orgId} />;
  } else if (dpaType == "DATA_SELLING_GOOGLE_ANALYTICS") {
    return <DPAWhyAmISeeingThisCardForGoogleAnalytics orgId={orgId} />;
  } else if (dpaType == "TARGETED_ADVERTISING") {
    return <DPAWhyAmISeeingThisCardForTargetedAdvertising orgId={orgId} />;
  } else if (dpaType == "SENSITIVE_DATA") {
    return <DPAWhyAmISeeingThisCardWithSensitiveDataPICs dataMap={dataMap} />;
  } else if (dpaType == "SNI") {
    return <DPAWhyAmISeeingThisCardForSNI />;
  }

  return null;
};

const DPAWhyAmISeeingThisCardForTargetedAdvertising = ({ orgId }: { orgId: string }) => {
  const [organizationVendors, organizationVendorsRequest] = useOrganizationVendors(orgId, [], true);

  if (organizationVendorsRequest.success === false) {
    return null;
  }

  // duplicated logic from OptOutService.getOptOutAttributes() in backend
  const anySelling = organizationVendors?.some(
    (v) => v.ccpaIsSelling && v.classification?.slug != VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER,
  );
  const anySharing = organizationVendors?.some((v) => v.ccpaIsSharing || v.usesCustomAudience);

  if (!anySelling || !anySharing) {
    return null;
  }

  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>Why Am I Seeing Targeted Advertising DPA?</p>
      </div>
      <div className="tip-content">
        <p>
          Based on your Data Map, your business engages in consumer behavioral advertising via one
          or more of its ad networks. This may be limited to browser-based internet tracking, but
          more commonly businesses also target customers by creating targeted audience lists that
          are shared with ad networks. This data practice requires an assessment. Follow the prompts
          below to get started.
        </p>
      </div>
    </div>
  );
};

const DPAWhyAmISeeingThisCardForGoogleAnalytics = ({ orgId }: { orgId: string }) => {
  const { answers, request: surveyRequest } = useSurvey(orgId, SELLING_AND_SHARING_SURVEY_NAME);
  const [organizationVendors, organizationVendorsRequest] = useOrganizationVendors(orgId, [], true);

  if (surveyRequest.success === false || organizationVendorsRequest.success === false) {
    return null;
  }

  const hasGoogleAnalytics = organizationVendors?.some((s) => isGoogleAnalytics(s));
  // The survey question asks if GA Data Sharing has been turned *off*, so we need to check if the answer is "false"
  const isGaSharingEnabled = answers?.["google-analytics-data-sharing"] == "false";

  if (!hasGoogleAnalytics || !isGaSharingEnabled) {
    return null;
  }

  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>Why Am I Seeing Data Selling - Google Analytics?</p>
      </div>
      <div className="tip-content">
        <p>
          Based on your Data Map, your business uses Google Analytics to track data about its
          website visitors. By default, Google Analytics accounts share consumer data with Google
          products and services, which is considered "selling" data. If your business{" "}
          <a
            href="https://support.google.com/analytics/answer/1011397"
            target="_blank"
            rel="noreferrer"
          >
            turns off this data sharing setting
          </a>
          , using Google Analytics is no longer considered a "sale." Your settings indicate that
          data sharing is on for Google Analytics, which means your business is “selling” data and
          will need to complete an assessment regarding this practice. Follow the prompts below to
          get started.
        </p>
      </div>
    </div>
  );
};

const DPAWhyAmISeeingThisCardWithDataSellingVendors = ({ orgId }: { orgId: string }) => {
  const [isShowingVendors, setIsShowingVendors] = useState(false);
  const [organizationVendors, organizationVendorsRequest] = useOrganizationVendors(orgId, [], true);

  if (organizationVendorsRequest.success === false) {
    return null;
  }

  // Since there is a separate DPA for Google Analytics, we only want to show vendors that are selling data and are not Google Analytics
  const sellingVendorsWithoutGoogleAnalytics = (organizationVendors || [])
    .filter((vendor) => vendor.ccpaIsSelling === true)
    .filter((vendor) => !isGoogleAnalytics(vendor));

  // If no applicable selling vendors, then don't show the tip box (since user must have manually created the DPA)
  if (sellingVendorsWithoutGoogleAnalytics.length === 0) {
    return null;
  }

  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>Why Am I Seeing Data Selling - Other DPA?</p>
      </div>
      <div className="tip-content">
        <p>
          Based on your Data Map, your business engages in data selling outside of the use of Google
          Analytics. View your list of your data selling vendors below.
        </p>
        {isShowingVendors && (
          <div>
            {sellingVendorsWithoutGoogleAnalytics?.map((vendor) => (
              <div key={vendor.id} className="dpa-tips--data-selling-vendor-card">
                <DataRecipientLogo
                  logoUrl={vendor.logoUrl || "/assets/images/icon-vendors-black.svg"}
                  size={32}
                />
                <div className="dpa-tips--data-selling-vendor-name">{vendor.name}</div>
              </div>
            ))}
          </div>
        )}
        <p>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setIsShowingVendors((s) => !s);
            }}
          >
            {isShowingVendors ? "Hide vendors" : "Show vendors"}
          </a>
        </p>
      </div>
    </div>
  );
};

const collectedSensitivePicsFromNonEmploymentGroups = (dataMap: DataMapDto): CategoryDto[] => {
  if (!dataMap) return [];

  let collectedPicsFromNonEmploymentGroups = collectedPICsFromFilteredCollection(dataMap, {
    exclude: { collectionGroupType: "EMPLOYMENT" },
  });

  return getSensitivePICsWithFilters(dataMap, {
    filterPicIds: collectedPicsFromNonEmploymentGroups.map((pic) => pic.id),
    // Match the logic in DataProtectionAssessmentTaskSensitiveData.isAnyPicSensitive() by *not* considering the "Inferences based on sensitive information" PIC as sensitive
    includeInferencesBasedOnSensitiveInformationPIC: false,
    excludeIfOnlySensitiveInCA: true,
  });
};

const DPAWhyAmISeeingThisCardWithSensitiveDataPICs = ({ dataMap }: { dataMap: DataMapDto }) => {
  const sensitivePics = collectedSensitivePicsFromNonEmploymentGroups(dataMap);

  if (!sensitivePics || sensitivePics.length == 0) {
    return null;
  }

  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>Why Am I Seeing Sensitive Data DPA?</p>
      </div>
      <div className="tip-content">
        <p>
          Based on your Data Map, your business handles the following personal information that is
          considered sensitive:{" "}
        </p>
        <ul>
          {sensitivePics.map((pic) => (
            <li key={pic.id}>{pic.name}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const DPAWhyAmISeeingThisCardForSNI = () => {
  return (
    <div className="tip-wrapper">
      <div className="header">
        <img className="star" src="/assets/images/stay_compliant/star.svg" alt="star" />
        <p>Why Am I Seeing Shopify Network Intelligence DPA?</p>
      </div>
      <div className="tip-content">
        <p>
          Based on your Data Map, your business uses Shopify Network Intelligence (SNI). When SNI is
          enabled, a DPA is required because Shopify is acting as a marketing cooperative to "sell"
          customer data and/or use it for purposes of targeting ads to your customers. Follow the
          prompts below to get started.
        </p>
      </div>
    </div>
  );
};
